import React, { useEffect, useState } from 'react';
import { ArrowDownTrayIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { generateChapter7ServicePacket } from '../forms/ScheduleDEF';


const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function apiUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

function downloadBlob(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

async function errorMessage(response, fallback) {
  const payload = await response.json().catch(() => null);
  return payload?.detail || payload?.error || fallback;
}

export default function ServiceDocumentsPanel({
  caseId,
  caseNumber,
  chapter,
  serviceDocs = false,
  allowMarkComplete = false,
  onCompleted,
}) {
  const [isComplete, setIsComplete] = useState(Boolean(serviceDocs));
  const [generating, setGenerating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [serviceRequirements, setServiceRequirements] = useState(null);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsError, setRequirementsError] = useState('');
  const normalizedChapter = String(chapter || '').trim();

  useEffect(() => {
    setIsComplete(Boolean(serviceDocs));
  }, [serviceDocs]);

  useEffect(() => {
    let cancelled = false;
    if (normalizedChapter !== '13') {
      setServiceRequirements(null);
      setRequirementsError('');
      return undefined;
    }

    const loadRequirements = async () => {
      setRequirementsLoading(true);
      setRequirementsError('');
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          apiUrl(`/case/${caseId}/service-documents/chapter-13-packet/`),
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!response.ok) {
          throw new Error(await errorMessage(response, 'Could not read the plan’s service requirements.'));
        }
        const payload = await response.json();
        if (!cancelled) setServiceRequirements(payload);
      } catch (caughtError) {
        if (!cancelled) setRequirementsError(caughtError.message);
      } finally {
        if (!cancelled) setRequirementsLoading(false);
      }
    };

    loadRequirements();
    return () => { cancelled = true; };
  }, [caseId, normalizedChapter]);

  const generatePacket = async () => {
    setGenerating(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let packet;
      let filename;

      if (normalizedChapter === '7') {
        const [caseResponse, creditorResponse] = await Promise.all([
          fetch(apiUrl(`/case/${caseId}/creditors/case-details/`), { headers }),
          fetch(apiUrl(`/case/${caseId}/creditors/`), { headers }),
        ]);
        if (!caseResponse.ok) {
          throw new Error(await errorMessage(caseResponse, 'Could not load the case details.'));
        }
        if (!creditorResponse.ok) {
          throw new Error(await errorMessage(creditorResponse, 'Could not load the creditors.'));
        }

        const caseInfo = await caseResponse.json();
        const creditors = await creditorResponse.json();
        packet = await generateChapter7ServicePacket({
          caseInfo,
          debtsD: creditors.filter((creditor) => creditor.debt_type === 'secured'),
          debtsE: creditors.filter((creditor) => creditor.debt_type === 'priority'),
          debtsF: creditors.filter((creditor) => creditor.debt_type === 'unsecured'),
          debtsG: creditors.filter((creditor) => creditor.debt_type === 'executory'),
          notifyEF: creditors.filter((creditor) => creditor.additional_notified),
          allDebts: creditors,
          amended: false,
          amendedReason: '',
          amendedCreditors: [],
        });
        filename = `${caseNumber || caseId}-statement-of-intent-service-documents.zip`;
      } else if (normalizedChapter === '13') {
        const response = await fetch(
          apiUrl(`/case/${caseId}/service-documents/chapter-13-packet/`),
          { method: 'POST', headers },
        );
        if (!response.ok) {
          throw new Error(await errorMessage(response, 'Could not create the Chapter 13 service packet.'));
        }
        packet = await response.blob();
        const disposition = response.headers.get('Content-Disposition') || '';
        const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
        filename = filenameMatch?.[1] || `${caseNumber || caseId}-chapter-13-service-documents.zip`;
        const recipientCount = Number(response.headers.get('X-Service-Recipient-Count'));
        const certifiedCount = Number(response.headers.get('X-Certified-Service-Count'));
        if (Number.isFinite(recipientCount)) {
          const certifiedMessage = Number.isFinite(certifiedCount) && certifiedCount > 0
            ? ` ${certifiedCount} envelope${certifiedCount === 1 ? '' : 's'} must be mailed by certified mail.`
            : '';
          setMessage(
            recipientCount > 0
              ? `Packet created with ${recipientCount} envelope${recipientCount === 1 ? '' : 's'}.${certifiedMessage}`
              : 'Packet created with the plan only; no mailed recipients were listed on the Certificate of Service.',
          );
        }
      } else {
        throw new Error('Service packets are available only for Chapter 7 and Chapter 13 cases.');
      }

      downloadBlob(packet, filename);
      if (normalizedChapter === '7') {
        setMessage('Statement of Intent and envelope packet downloaded.');
      }
    } catch (caughtError) {
      setError(caughtError.message || 'Could not create the service packet.');
    } finally {
      setGenerating(false);
    }
  };

  const markComplete = async () => {
    setCompleting(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(apiUrl(`/case/${caseId}/`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ service_docs: true }),
      });
      if (!response.ok) {
        throw new Error(await errorMessage(response, 'Could not mark service documents complete.'));
      }
      const updatedCase = await response.json();
      setIsComplete(true);
      setMessage('Filing service documents marked complete.');
      onCompleted?.(updatedCase);
    } catch (caughtError) {
      setError(caughtError.message || 'Could not mark service documents complete.');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 text-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-semibold text-slate-900">
            {normalizedChapter === '7' ? 'Statement of Intent service packet' : 'Chapter 13 plan service packet'}
          </h4>
          <p className="mt-1 text-sm leading-6">
            {normalizedChapter === '7'
              ? 'Downloads the Statement of Intent and envelopes for the creditors being served.'
              : 'Downloads the filed plan and creates envelopes only for the mailed recipients listed on its Certificate of Service.'}
          </p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isComplete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
          {isComplete ? 'Complete' : 'Not yet done'}
        </span>
      </div>

      {normalizedChapter === '13' && requirementsLoading && (
        <p className="mt-3 text-sm text-slate-500">Reading the filed plan’s mailing requirements…</p>
      )}
      {normalizedChapter === '13' && requirementsError && (
        <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
          {requirementsError}
        </p>
      )}
      {normalizedChapter === '13' && serviceRequirements && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
          <h5 className="text-sm font-semibold text-slate-900">Mailing requirements from the Certificate of Service</h5>
          {serviceRequirements.recipients?.length ? (
            <>
              <p className="mt-1 text-xs text-slate-600">The downloaded packet will include an envelope for every recipient below.</p>
              <ul className="mt-2 space-y-2">
                {serviceRequirements.recipients.map((recipient, index) => {
                  const certified = String(recipient.method || '').toLowerCase().startsWith('certified');
                  return (
                    <li key={`${recipient.name}-${index}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="font-medium text-slate-800">{recipient.name}</span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${certified ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300' : 'bg-blue-50 text-blue-800 ring-1 ring-blue-200'}`}>
                        {certified ? 'Certified mail required' : 'U.S. Mail'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="mt-1 text-sm text-slate-600">No mailed recipients are listed.</p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={generatePacket}
          disabled={generating}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          {generating ? 'Creating packet…' : 'Download service packet'}
        </button>
        {allowMarkComplete && !isComplete && (
          <button
            type="button"
            onClick={markComplete}
            disabled={completing}
            className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
          >
            <CheckCircleIcon className="h-5 w-5" />
            {completing ? 'Saving…' : 'Mark service complete'}
          </button>
        )}
      </div>
      {message && <p className="mt-3 text-sm font-medium text-green-700">{message}</p>}
      {error && <p className="mt-3 text-sm font-medium text-red-700">{error}</p>}
    </section>
  );
}
